import React, { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export interface EDSSwipeoutOption {
  name: string;
  icon: React.ReactNode;
  label: string;
  accent: "teal" | "blue" | "red" | "green";
  customColor?: string; // Optional custom hex colour (e.g., "#00ff00")
}

export interface EDSSwipeoutProps {
  children: React.ReactNode;
  leftOptions?: EDSSwipeoutOption[];
  rightOptions?: EDSSwipeoutOption[];
  onOptionClick?: (optionName: string) => void;
  style?: React.CSSProperties;
  tabIndex?: number;
  className?: string;
}

const MIN_SWIPE_DISTANCE = 30;
const MAX_SWIPE_DISTANCE = 80;
const FULL_SWIPE_THRESHOLD = MAX_SWIPE_DISTANCE * 1.2;
const DIRECTION_LOCK_THRESHOLD = 5;

interface SwipeResult {
  targetOffset: number;
  shouldExecuteAction: boolean;
  shouldDock: boolean;
  shouldReset: boolean;
}

const resolveSwipeResult = ({
  isUndockSwipe,
  isRightSwipe,
  isLeftSwipe,
  isFullSwipe,
  hasLeft,
  hasRight,
  maxDistance
}: {
  isUndockSwipe: boolean;
  isRightSwipe: boolean;
  isLeftSwipe: boolean;
  isFullSwipe: boolean;
  hasLeft: boolean;
  hasRight: boolean;
  maxDistance: number;
}): SwipeResult => {
  if (isUndockSwipe) {
    return {
      targetOffset: 0,
      shouldExecuteAction: false,
      shouldDock: false,
      shouldReset: true
    };
  }
  if (isRightSwipe && hasLeft) {
    return {
      targetOffset: maxDistance,
      shouldExecuteAction: isFullSwipe,
      shouldDock: !isFullSwipe,
      shouldReset: false
    };
  }
  if (isLeftSwipe && hasRight) {
    return {
      targetOffset: -maxDistance,
      shouldExecuteAction: isFullSwipe,
      shouldDock: !isFullSwipe,
      shouldReset: false
    };
  }
  return {
    targetOffset: 0,
    shouldExecuteAction: false,
    shouldDock: false,
    shouldReset: false
  };
};

export const EDSSwipeout: React.FC<EDSSwipeoutProps> = ({
  children,
  leftOptions = [],
  rightOptions = [],
  onOptionClick,
  style,
  tabIndex = 0,
  className = ""
}) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDocked, setIsDocked] = useState(false);
  const [isSwiping, setIsSwiping] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Refs for synchronous access to touch positions in event handlers.
  // React state updates are asynchronous, so if touchmove fires before
  // the re-render from touchstart's setState, the callback closure
  // still sees touchStart as null and the swipe is never processed.
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchEndRef = useRef<{ x: number; y: number } | null>(null);
  const swipeDirectionRef = useRef<"none" | "horizontal" | "vertical">("none");

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const start = {
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY
      };
      touchStartRef.current = start;
      touchEndRef.current = null;
      swipeDirectionRef.current = "none";
      setIsSwiping(true);

      // If currently docked and user starts a new swipe, reset docking
      if (isDocked) {
        setIsDocked(false);
        setSwipeOffset(0);
      }

      // Prevent touch events from bubbling to parent list
      e.stopPropagation();
    },
    [isDocked]
  );

  // Non-passive native touchmove listener to call preventDefault() for horizontal swipes.
  // This prevents the browser from taking over the touch sequence for native scrolling
  // when the gesture is primarily horizontal, which would cause touchcancel and stop
  // JavaScript from receiving subsequent touchmove events.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleNativeTouchMove = (e: TouchEvent) => {
      const start = touchStartRef.current;
      const touch = e.touches?.[0] ?? e.targetTouches?.[0];
      if (!start || !touch) return;

      const dx = Math.abs(touch.clientX - start.x);
      const dy = Math.abs(touch.clientY - start.y);

      if (
        swipeDirectionRef.current === "none" &&
        (dx > DIRECTION_LOCK_THRESHOLD || dy > DIRECTION_LOCK_THRESHOLD)
      ) {
        swipeDirectionRef.current = dx >= dy ? "horizontal" : "vertical";
      }

      if (swipeDirectionRef.current === "horizontal") {
        e.preventDefault();
      }
    };

    container.addEventListener("touchmove", handleNativeTouchMove, {
      passive: false
    });

    return () => {
      container.removeEventListener("touchmove", handleNativeTouchMove);
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const currentTouch = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };
    touchEndRef.current = currentTouch;

    const start = touchStartRef.current;
    if (start === null) return;

    // Determine direction if not yet locked (fallback for environments
    // where the native touchmove listener cannot read touch coordinates)
    if (swipeDirectionRef.current === "none") {
      const dx = Math.abs(currentTouch.x - start.x);
      const dy = Math.abs(currentTouch.y - start.y);
      if (dx > DIRECTION_LOCK_THRESHOLD || dy > DIRECTION_LOCK_THRESHOLD) {
        swipeDirectionRef.current = dx >= dy ? "horizontal" : "vertical";
      }
    }

    if (swipeDirectionRef.current === "horizontal") {
      const horizontalDistance = currentTouch.x - start.x;
      const clampedDistance = Math.max(
        -MAX_SWIPE_DISTANCE,
        Math.min(MAX_SWIPE_DISTANCE, horizontalDistance)
      );
      setSwipeOffset(clampedDistance);
      e.stopPropagation();
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsSwiping(false);

    const start = touchStartRef.current;
    const end = touchEndRef.current;
    const direction = swipeDirectionRef.current;
    swipeDirectionRef.current = "none";

    if (!start || !end) {
      if (!isDocked) {
        setSwipeOffset(0);
      }
      return;
    }

    const horizontalDistance = end.x - start.x;
    const horizontalDistanceAbs = Math.abs(horizontalDistance);

    if (
      direction !== "horizontal" ||
      horizontalDistanceAbs <= MIN_SWIPE_DISTANCE
    ) {
      setTimeout(() => {
        setSwipeOffset(0);
        setIsDocked(false);
      }, 100);
      return;
    }

    const isRightSwipe = horizontalDistance > MIN_SWIPE_DISTANCE;
    const isLeftSwipe = horizontalDistance < -MIN_SWIPE_DISTANCE;
    const isFullSwipe = horizontalDistanceAbs > FULL_SWIPE_THRESHOLD;

    const isUndockSwipe =
      isDocked &&
      ((swipeOffset > 0 && isLeftSwipe) || (swipeOffset < 0 && isRightSwipe));

    const { targetOffset, shouldExecuteAction, shouldDock, shouldReset } =
      resolveSwipeResult({
        isUndockSwipe,
        isRightSwipe,
        isLeftSwipe,
        isFullSwipe,
        hasLeft: leftOptions.length > 0,
        hasRight: rightOptions.length > 0,
        maxDistance: MAX_SWIPE_DISTANCE
      });

    if (shouldExecuteAction) {
      const targetOptions = isRightSwipe ? leftOptions : rightOptions;
      if (targetOptions.length > 0) {
        onOptionClick?.(targetOptions[0].name);
      }
      setTimeout(() => {
        setSwipeOffset(0);
        setIsDocked(false);
      }, 100);
      return;
    }

    if (shouldReset) {
      setSwipeOffset(0);
      setIsDocked(false);
      return;
    }

    if (shouldDock) {
      setSwipeOffset(targetOffset);
      setIsDocked(true);
      return;
    }

    setTimeout(() => {
      setSwipeOffset(0);
      setIsDocked(false);
    }, 300);
  }, [leftOptions, rightOptions, onOptionClick, isDocked, swipeOffset]);

  const handleTouchCancel = useCallback(() => {
    setIsSwiping(false);
    swipeDirectionRef.current = "none";
    if (!isDocked) {
      setSwipeOffset(0);
    }
  }, [isDocked]);

  const handleOptionClick = useCallback(
    (option: EDSSwipeoutOption, event?: React.MouseEvent) => {
      // Prevent event bubbling to parent elements
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      onOptionClick?.(option.name);
      // Reset position and docking state after option click
      setSwipeOffset(0);
      setIsDocked(false);
    },
    [onOptionClick]
  );

  const getAccentColor = (accent: string) => {
    switch (accent) {
      case "teal":
        return "var(--accent-color, #1fd4d4)";
      case "blue":
        return "var(--primary, #3b82f6)";
      case "red":
        return "var(--destructive, #ef4444)";
      case "green":
        return "var(--success, #10b981)";
      default:
        return "var(--accent-color, #1fd4d4)";
    }
  };

  const hasLeftOptions = leftOptions.length > 0;
  const hasRightOptions = rightOptions.length > 0;

  // Handle clicking on the main content to reset docking
  const handleContentClick = useCallback(() => {
    if (isDocked) {
      setIsDocked(false);
      setSwipeOffset(0);
    }
  }, [isDocked]);

  // Handle keyboard events for accessibility (S1082)
  const handleContentKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (
        isDocked &&
        (e.key === "Enter" || e.key === " " || e.key === "Escape")
      ) {
        e.preventDefault();
        setIsDocked(false);
        setSwipeOffset(0);
      }
    },
    [isDocked]
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        "eds-swipeout",
        "relative w-full h-full overflow-hidden shadow-none",
        "bg-panel-background",
        className
      )}
      style={style}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    >
      {/* Left Options - Revealed when swiping LEFT-TO-RIGHT (positive swipeOffset) */}
      {hasLeftOptions && (
        <div
          className={cn(
            "eds-swipeout__left-options absolute h-full min-h-[60px] flex z-[1] pointer-events-auto",
            !isSwiping && "transition-opacity duration-200 ease-out"
          )}
          style={{
            opacity:
              swipeOffset > 0
                ? Math.min(1, swipeOffset / MAX_SWIPE_DISTANCE)
                : 0
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {leftOptions.map((option, index) => (
            <button
              key={`left-${option.name}-${index}`}
              className="eds-swipeout__option eds-swipeout__option--left relative flex items-center justify-center w-full h-full border-none rounded cursor-pointer transition-all duration-200 ease-in-out overflow-hidden"
              style={{
                backgroundColor:
                  option.customColor ?? getAccentColor(option.accent)
              }}
              onClick={(event: React.MouseEvent) =>
                handleOptionClick(option, event)
              }
            >
              <div className="eds-swipeout__option-overlay absolute inset-0 bg-white/20 z-0 pointer-events-none rounded-[inherit]" />
              <div className="eds-swipeout__option-content relative z-[1] flex flex-col items-center justify-center gap-1 p-2 text-center text-black">
                {option.icon}
                <span className="eds-swipeout__option-label text-xs font-bold leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[60px] text-black">
                  {option.label}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Right Options - Revealed when swiping RIGHT-TO-LEFT (negative swipeOffset) */}
      {hasRightOptions && (
        <div
          className={cn(
            "eds-swipeout__right-options absolute h-full min-h-[60px] flex z-[1] pointer-events-auto",
            !isSwiping && "transition-opacity duration-200 ease-out"
          )}
          style={{
            opacity:
              swipeOffset < 0
                ? Math.min(1, Math.abs(swipeOffset) / MAX_SWIPE_DISTANCE)
                : 0
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
          {rightOptions.map((option, index) => (
            <button
              key={`right-${option.name}-${index}`}
              className="eds-swipeout__option eds-swipeout__option--right relative flex items-center justify-center w-full h-full border-none rounded cursor-pointer transition-all duration-200 ease-in-out overflow-hidden"
              style={{
                backgroundColor:
                  option.customColor ?? getAccentColor(option.accent)
              }}
              onClick={(event: React.MouseEvent) =>
                handleOptionClick(option, event)
              }
            >
              <div className="eds-swipeout__option-overlay absolute inset-0 bg-white/20 z-0 pointer-events-none rounded-[inherit]" />
              <div className="eds-swipeout__option-content relative z-[1] flex flex-col items-center justify-center gap-1 p-2 text-center text-black">
                {option.icon}
                <span className="eds-swipeout__option-label text-xs font-bold leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-[60px] text-black">
                  {option.label}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Main Content */}
      <div
        ref={contentRef}
        className={cn(
          "eds-swipeout__content relative z-[2] w-full h-full bg-panel-background will-change-transform flex flex-col",
          !isSwiping && "transition-transform duration-200 ease-out"
        )}
        style={{
          transform: `translateX(${swipeOffset}px)`
        }}
        tabIndex={tabIndex}
      >
        {children}
        {/* Close overlay button - native button for accessibility (S6819, S6848) */}
        {isDocked && (
          <button
            type="button"
            className="eds-swipeout__close-overlay absolute inset-0 z-[3] w-full h-full bg-transparent border-none cursor-pointer"
            aria-label="Close swipe options"
            onClick={handleContentClick}
            onKeyDown={handleContentKeyDown}
          />
        )}
      </div>
    </div>
  );
};
